import React, { useEffect, useState } from 'react';
import { useQuery, keepPreviousData } from '@tanstack/react-query';
import { MagnifyingGlassIcon } from '@heroicons/react/24/outline';
import api from '../../services/api';
import { Modal } from '../../components/ui/Modal';
import { Pagination } from '../../components/ui/Pagination';
import { badgeClass, formatMoney, formatNumber, getApiErrorMessage, inputClass, secondaryButtonClass } from '../../components/ui/FormControls';
import { cn } from '../../lib/utils';

type BucketKey = 'd0_30' | 'd31_60' | 'd61_90' | 'd91_180' | 'over180';
type Sort = 'value' | 'age' | 'name' | 'difference';

interface Row {
  productId: string;
  name: string;
  sku: string;
  category: string | null;
  unit: string | null;
  onHand: number;
  costPrice: number;
  fifoValue: number;
  fifoUnitCost: number;
  costPriceValue: number;
  difference: number;
  oldestDate: string | null;
  oldestAgeDays: number | null;
  estimatedValue: number;
  buckets: Record<BucketKey, { quantity: number; value: number }>;
  notes: string[];
  layerCount?: number;
}
interface Layer {
  date: string;
  ageDays: number;
  bucket: BucketKey;
  quantity: number;
  unitCost: number;
  value: number;
  source: string;
  estimated: boolean;
}
interface Detail extends Row {
  asOf: string;
  layers: Layer[];
}
interface Page {
  asOf: string;
  data: Row[];
  total: number;
  totalPages: number;
}
interface Summary {
  asOf: string;
  products: number;
  fifoValue: number;
  costPriceValue: number;
  difference: number;
  estimatedValue: number;
  productsWithLedgerGaps: number;
  buckets: { key: BucketKey; label: string; value: number; share: number; products: number }[];
}

const PAGE_SIZES = [10, 25, 50, 100];
const thClass = 'px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide text-gray-500';
const BUCKETS: { key: BucketKey; short: string; label: string; bar: string; text: string }[] = [
  { key: 'd0_30', short: '0–30', label: '0 to 30 days', bar: 'bg-green-500', text: 'text-gray-700' },
  { key: 'd31_60', short: '31–60', label: '31 to 60 days', bar: 'bg-lime-500', text: 'text-gray-700' },
  { key: 'd61_90', short: '61–90', label: '61 to 90 days', bar: 'bg-amber-400', text: 'text-amber-700' },
  { key: 'd91_180', short: '91–180', label: '91 to 180 days', bar: 'bg-orange-500', text: 'text-orange-700' },
  { key: 'over180', short: '180+', label: 'Over 180 days', bar: 'bg-red-500', text: 'text-red-600' },
];
const today = () => {
  const now = new Date();
  return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-${String(now.getDate()).padStart(2, '0')}`;
};
const formatDate = (value?: string | null) => (value ? new Date(`${value}T00:00:00`).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' }) : '—');
const clean = (params: object) => Object.fromEntries(Object.entries(params).filter(([, value]) => value !== undefined && value !== ''));
const signed = (value: number) => `${value > 0 ? '+' : value < 0 ? '−' : ''}${formatMoney(Math.abs(value))}`;

const valuationApi = {
  page: async (params: object): Promise<Page> => (await api.get('/stock-valuation', { params: clean(params) })).data,
  summary: async (asOf: string): Promise<Summary> => (await api.get('/stock-valuation/summary', { params: clean({ asOf }) })).data,
  one: async (productId: string, asOf: string): Promise<Detail> => (await api.get(`/stock-valuation/${productId}`, { params: clean({ asOf }) })).data,
};

// ---------------------------------------------------------------- the layers of one product

const LayersPanel: React.FC<{ productId: string; asOf: string; onClose: () => void }> = ({ productId, asOf, onClose }) => {
  const { data, isLoading, isError, error } = useQuery({ queryKey: ['stock-valuation', 'one', productId, asOf], queryFn: () => valuationApi.one(productId, asOf) });
  if (isLoading) return <p className="py-10 text-center text-sm text-gray-500">Working it out…</p>;
  if (isError || !data) return <p className="py-10 text-center text-sm text-gray-700">{getApiErrorMessage(error, 'The layers could not be worked out.')}</p>;
  const facts = [
    { label: 'In stock', value: `${formatNumber(data.onHand)} ${data.unit ?? ''}` },
    { label: 'Value, first in first out', value: formatMoney(data.fifoValue) },
    { label: 'Per unit', value: formatMoney(data.fifoUnitCost) },
    { label: 'At the cost price on the item', value: `${formatMoney(data.costPriceValue)} (${formatMoney(data.costPrice)} each)` },
  ];
  return (
    <div className="space-y-4">
      <p className="text-sm text-gray-700">{data.sku}{data.category ? ` · ${data.category}` : ''} · as at the end of {formatDate(data.asOf)}</p>
      <div className="grid grid-cols-2 gap-3 rounded-lg bg-gray-50 p-3 text-sm ring-1 ring-gray-200 sm:grid-cols-4">
        {facts.map((fact) => (<div key={fact.label}><p className="text-xs uppercase tracking-wide text-gray-500">{fact.label}</p><p className="font-medium text-gray-900">{fact.value}</p></div>))}
      </div>
      {data.notes.length > 0 && (
        <ul className="space-y-1 rounded-lg bg-amber-50 p-3 text-sm text-amber-900 ring-1 ring-amber-600/20">
          {data.notes.map((note) => (<li key={note}>{note}</li>))}
        </ul>
      )}
      {data.layers.length === 0 ? (
        <p className="text-sm text-gray-500">Nothing was in stock at the end of that day.</p>
      ) : (
        <div className="overflow-x-auto rounded-lg border border-gray-200">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th scope="col" className={thClass}>Came in</th>
                <th scope="col" className={thClass}>From</th>
                <th scope="col" className={cn(thClass, 'text-right')}>Still in stock</th>
                <th scope="col" className={cn(thClass, 'text-right')}>Cost each</th>
                <th scope="col" className={cn(thClass, 'text-right')}>Value</th>
                <th scope="col" className={cn(thClass, 'text-right')}>Age</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {data.layers.map((layer, index) => {
                const bucket = BUCKETS.find((candidate) => candidate.key === layer.bucket) ?? BUCKETS[0];
                return (
                  <tr key={index}>
                    <td className="whitespace-nowrap px-4 py-2 text-sm text-gray-700">{formatDate(layer.date)}</td>
                    <td className="px-4 py-2 text-sm text-gray-900">{layer.source}{layer.estimated && <span className={cn(badgeClass, 'ml-2 bg-amber-50 text-amber-800 ring-amber-600/20')}>at cost price</span>}</td>
                    <td className="whitespace-nowrap px-4 py-2 text-right text-sm text-gray-900 tabular-nums">{formatNumber(layer.quantity)}</td>
                    <td className="whitespace-nowrap px-4 py-2 text-right text-sm text-gray-700 tabular-nums">{formatMoney(layer.unitCost)}</td>
                    <td className="whitespace-nowrap px-4 py-2 text-right text-sm font-medium text-gray-900 tabular-nums">{formatMoney(layer.value)}</td>
                    <td className={cn('whitespace-nowrap px-4 py-2 text-right text-sm tabular-nums', bucket.text)}>{formatNumber(layer.ageDays)} days</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
      <p className="text-xs text-gray-500">The oldest stock is at the top; the next issue takes from there.</p>
      <div className="flex justify-end border-t border-gray-200 pt-3">
        <button type="button" onClick={onClose} className={secondaryButtonClass}>Close</button>
      </div>
    </div>
  );
};

// ---------------------------------------------------------------- the page

const StockValuation: React.FC = () => {
  const [asOf, setAsOf] = useState('');
  const [bucket, setBucket] = useState<BucketKey | ''>('');
  const [sort, setSort] = useState<Sort>('value');
  const [searchInput, setSearchInput] = useState('');
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(25);
  const [viewing, setViewing] = useState<Row | null>(null);

  useEffect(() => {
    const timer = setTimeout(() => {
      setSearch(searchInput.trim());
      setPage(1);
    }, 300);
    return () => clearTimeout(timer);
  }, [searchInput]);

  const valid = asOf === '' || asOf <= today();
  const { data, isLoading, isError, error, isFetching } = useQuery({
    queryKey: ['stock-valuation', 'page', { asOf, bucket, sort, search, page, limit }],
    queryFn: () => valuationApi.page({ asOf, bucket, sort, search, page, limit }),
    placeholderData: keepPreviousData,
    enabled: valid,
  });
  const { data: summary } = useQuery({ queryKey: ['stock-valuation', 'summary', asOf], queryFn: () => valuationApi.summary(asOf), enabled: valid });

  const rows = data?.data ?? [];
  const total = data?.total ?? 0;
  const totalPages = data?.totalPages ?? 1;
  const firstRow = total > 0 ? (page - 1) * limit + 1 : 0;
  const lastRow = Math.min(page * limit, total);
  const old = (summary?.buckets ?? []).filter((item) => item.key === 'd91_180' || item.key === 'over180').reduce((sum, item) => sum + item.value, 0);
  const over180 = summary?.buckets.find((item) => item.key === 'over180');
  const stats = [
    { label: 'Stock value, first in first out', value: formatMoney(summary?.fifoValue ?? 0), hint: `${formatNumber(summary?.products ?? 0)} product(s) in stock` },
    { label: 'At the cost price on the items', value: formatMoney(summary?.costPriceValue ?? 0), hint: `FIFO is ${signed(summary?.difference ?? 0)} against it` },
    { label: 'Older than 90 days', value: formatMoney(old), hint: `${formatMoney(over180?.value ?? 0)} of it older than 180 days`, alert: old > 0 },
    { label: 'Valued at cost price', value: formatMoney(summary?.estimatedValue ?? 0), hint: `Stock the ledger has no cost for · ${formatNumber(summary?.productsWithLedgerGaps ?? 0)} product(s) with gaps` },
  ];

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-semibold text-gray-900">Stock Valuation &amp; Ageing</h1>
        <p className="mt-1 text-sm text-gray-500">What the stock on hand cost, first in first out, and how long it has been lying. Worked out from the stock ledger: each receipt at its own cost, each issue from the oldest stock.</p>
      </div>

      <div className="grid grid-cols-2 gap-4 lg:grid-cols-4">
        {stats.map((stat) => (
          <div key={stat.label} className="rounded-lg bg-white p-4 shadow-sm ring-1 ring-gray-200">
            <p className="text-xs font-medium uppercase tracking-wide text-gray-500">{stat.label}</p>
            <p className={cn('mt-1 text-2xl font-semibold', stat.alert ? 'text-amber-700' : 'text-gray-900')}>{stat.value}</p>
            <p className="mt-1 text-xs text-gray-500">{stat.hint}</p>
          </div>
        ))}
      </div>

      {summary && summary.fifoValue > 0 && (
        <div className="rounded-lg bg-white p-4 shadow-sm ring-1 ring-gray-200">
          <h2 className="text-xs font-semibold uppercase tracking-wide text-gray-500">Value by age</h2>
          <div className="mt-3 flex h-3 overflow-hidden rounded-full bg-gray-100" role="img" aria-label="Stock value by age">
            {BUCKETS.map((item) => {
              const share = summary.buckets.find((candidate) => candidate.key === item.key)?.share ?? 0;
              return share > 0 ? <div key={item.key} className={item.bar} style={{ width: `${share}%` }} title={`${item.label}: ${share}%`} /> : null;
            })}
          </div>
          <div className="mt-3 grid grid-cols-2 gap-3 sm:grid-cols-5">
            {BUCKETS.map((item) => {
              const figures = summary.buckets.find((candidate) => candidate.key === item.key);
              return (
                <button key={item.key} type="button" onClick={() => { setBucket(bucket === item.key ? '' : item.key); setPage(1); }} aria-pressed={bucket === item.key} className={cn('rounded-md p-2 text-left ring-1 ring-inset', bucket === item.key ? 'bg-indigo-50 ring-indigo-300' : 'ring-gray-200 hover:bg-gray-50')}>
                  <span className="flex items-center gap-1.5 text-xs text-gray-500"><span className={cn('inline-block h-2 w-2 rounded-full', item.bar)} />{item.label}</span>
                  <span className="mt-0.5 block text-sm font-semibold text-gray-900 tabular-nums">{formatMoney(figures?.value ?? 0)}</span>
                  <span className="block text-xs text-gray-500">{figures?.share ?? 0}% · {formatNumber(figures?.products ?? 0)} product(s)</span>
                </button>
              );
            })}
          </div>
        </div>
      )}

      <div className="overflow-hidden rounded-lg bg-white shadow-sm ring-1 ring-gray-200">
        <div className="flex flex-col gap-3 border-b border-gray-200 p-4 lg:flex-row lg:items-center">
          <div className="relative flex-1">
            <MagnifyingGlassIcon className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
            <input type="search" value={searchInput} onChange={(event) => setSearchInput(event.target.value)} placeholder="Search by product, SKU or category" aria-label="Search products" className={cn(inputClass, 'pl-9')} />
          </div>
          <label className="flex items-center gap-2 whitespace-nowrap text-sm text-gray-700">
            As at the end of
            <input type="date" max={today()} value={asOf} onChange={(event) => { setAsOf(event.target.value); setPage(1); }} aria-label="As at" className={cn(inputClass, 'w-44 py-1.5')} />
          </label>
          <select value={bucket} onChange={(event) => { setBucket(event.target.value as BucketKey | ''); setPage(1); }} aria-label="Age" className={cn(inputClass, 'lg:w-48')}>
            <option value="">Stock of any age</option>
            {BUCKETS.map((item) => (<option key={item.key} value={item.key}>With stock {item.label.toLowerCase()}</option>))}
          </select>
          <select value={sort} onChange={(event) => { setSort(event.target.value as Sort); setPage(1); }} aria-label="Order" className={cn(inputClass, 'lg:w-52')}>
            <option value="value">Most valuable first</option>
            <option value="age">Oldest stock first</option>
            <option value="difference">Furthest from cost price first</option>
            <option value="name">By name</option>
          </select>
        </div>
        {!valid && <p className="border-b border-gray-200 px-4 py-3 text-sm text-red-600">Choose today or an earlier day.</p>}

        <div className={cn('overflow-x-auto', isFetching && data && 'opacity-60')}>
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th scope="col" className={thClass}>Product</th>
                <th scope="col" className={cn(thClass, 'text-right')}>In stock</th>
                <th scope="col" className={cn(thClass, 'text-right')}>FIFO value</th>
                <th scope="col" className={cn(thClass, 'text-right')}>At cost price</th>
                <th scope="col" className={thClass}>Oldest stock</th>
                {BUCKETS.map((item) => (<th key={item.key} scope="col" className={cn(thClass, 'text-right')}>{item.short}</th>))}
                <th scope="col" className={cn(thClass, 'text-right')}>Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {isError ? (
                <tr><td colSpan={11} className="px-4 py-10 text-center text-sm text-gray-700">{getApiErrorMessage(error, 'The valuation could not be worked out.')}</td></tr>
              ) : isLoading ? (
                <tr><td colSpan={11} className="px-4 py-10 text-center text-sm text-gray-500">Working it out…</td></tr>
              ) : rows.length === 0 ? (
                <tr>
                  <td colSpan={11} className="px-4 py-12 text-center">
                    <p className="text-sm font-medium text-gray-900">{search || bucket ? 'No product matches' : 'Nothing is in stock'}</p>
                    <p className="mt-1 text-sm text-gray-500">Only products with stock on hand are valued.</p>
                  </td>
                </tr>
              ) : (
                rows.map((row) => (
                  <tr key={row.productId} className="hover:bg-gray-50">
                    <td className="px-4 py-3">
                      <button type="button" onClick={() => setViewing(row)} className="text-left text-sm font-medium text-indigo-600 hover:text-indigo-500">{row.name}</button>
                      <p className="mt-0.5 text-xs text-gray-500">{row.sku}{row.notes.length > 0 && <span className="ml-2 text-amber-700">{row.estimatedValue >= row.fifoValue ? 'valued at cost price' : 'partly valued at cost price'}</span>}</p>
                    </td>
                    <td className="whitespace-nowrap px-4 py-3 text-right text-sm text-gray-700 tabular-nums">{formatNumber(row.onHand)} <span className="text-gray-500">{row.unit}</span></td>
                    <td className="whitespace-nowrap px-4 py-3 text-right text-sm font-medium text-gray-900 tabular-nums">{formatMoney(row.fifoValue)}<span className="block text-xs font-normal text-gray-500">{formatMoney(row.fifoUnitCost)} each</span></td>
                    <td className="whitespace-nowrap px-4 py-3 text-right text-sm text-gray-700 tabular-nums">{formatMoney(row.costPriceValue)}{Math.abs(row.difference) >= 0.01 && <span className={cn('block text-xs', row.difference > 0 ? 'text-green-700' : 'text-red-600')}>{signed(row.difference)}</span>}</td>
                    <td className="whitespace-nowrap px-4 py-3 text-sm text-gray-700">{formatDate(row.oldestDate)}<span className={cn('block text-xs', (row.oldestAgeDays ?? 0) > 180 ? 'text-red-600' : (row.oldestAgeDays ?? 0) > 90 ? 'text-orange-700' : 'text-gray-500')}>{formatNumber(row.oldestAgeDays ?? 0)} days</span></td>
                    {BUCKETS.map((item) => (
                      <td key={item.key} className={cn('whitespace-nowrap px-4 py-3 text-right text-sm tabular-nums', row.buckets[item.key].quantity > 0 ? item.text : 'text-gray-300')} title={row.buckets[item.key].quantity > 0 ? formatMoney(row.buckets[item.key].value) : undefined}>
                        {row.buckets[item.key].quantity > 0 ? formatNumber(row.buckets[item.key].quantity) : '—'}
                      </td>
                    ))}
                    <td className="whitespace-nowrap px-4 py-3 text-right">
                      <button type="button" onClick={() => setViewing(row)} className="rounded-md px-2.5 py-1 text-xs font-semibold text-indigo-600 ring-1 ring-inset ring-indigo-200 hover:bg-indigo-50" aria-label={`Layers of ${row.name}`}>Layers</button>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        <div className="flex flex-col gap-3 border-t border-gray-200 px-4 py-3 sm:flex-row sm:items-center sm:justify-between">
          <div className="flex flex-wrap items-center gap-x-4 gap-y-2 text-sm text-gray-600">
            <span>Showing {firstRow}–{lastRow} of {formatNumber(total)}</span>
            <label className="flex items-center gap-2">
              Rows per page
              <select value={limit} onChange={(event) => { setLimit(Number(event.target.value)); setPage(1); }} className="rounded-md border border-gray-300 bg-white py-1 pl-2 pr-7 text-sm focus:border-indigo-500 focus:outline-none focus:ring-1 focus:ring-indigo-500">
                {PAGE_SIZES.map((size) => (<option key={size} value={size}>{size}</option>))}
              </select>
            </label>
            <span className="text-xs text-gray-500">The age columns are quantities, in days since the stock came in</span>
          </div>
          {totalPages > 1 && <Pagination currentPage={page} totalPages={totalPages} onPageChange={setPage} />}
        </div>
      </div>

      <Modal isOpen={!!viewing} onClose={() => setViewing(null)} title={viewing?.name ?? 'Layers'} size="xl">
        {viewing && <LayersPanel key={`${viewing.productId}-${asOf}`} productId={viewing.productId} asOf={asOf} onClose={() => setViewing(null)} />}
      </Modal>
    </div>
  );
};

export default StockValuation;
